import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Warning } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import StandardTable from '../../components/ui/StandardTable';
import Textarea from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import WmsNavigation from '../../components/wms/WmsNavigation';
import { useAuth } from '../../hooks/useAuth';
import { useDebounce } from '../../hooks/useDebounce';
import { api } from '../../services/api';
import { ExtractPaging, ExtractResults } from '../../utils/aggregation';
import { FormatDateTimeBr } from '../../utils/date';
import { PURCHASING_STATUS_LABELS, WMS_STATUS_CONFIG } from '../../utils/status';

const DEFAULT_LIMIT = 15;

const DashboardPage = () => {
    const [outbounds, setOutbounds] = useState([]);
    const [paging, setPaging] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });
    const [inbounds, setInbounds] = useState([]);
    const [inboundPaging, setInboundPaging] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });
    const [inboundLoading, setInboundLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_LIMIT);
    const [inboundPage, setInboundPage] = useState(1);
    const [inboundPageSize, setInboundPageSize] = useState(DEFAULT_LIMIT);
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        outboundId: null,
        status: null,
        label: '',
    });
    const [cancelState, setCancelState] = useState({
        isOpen: false,
        outboundId: null,
        reason: '',
        confirmationText: '',
        files: [],
    });
    const [cancelling, setCancelling] = useState(false);
    const [receivingInbound, setReceivingInbound] = useState(false);
    const [inboundDetailsState, setInboundDetailsState] = useState({
        isOpen: false,
        row: null,
    });
    const [inboundReceiveState, setInboundReceiveState] = useState({
        isOpen: false,
        row: null,
        files: [],
        note: '',
    });
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const toast = useToast();
    const { checkPermission } = useAuth();
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const canUpdateStatus = checkPermission('wms:status:update');
    const canManageOutbound = checkPermission('wms:outbound:create');

    const fetchData = async ({
        pageToFetch = page,
        pageSizeToFetch = pageSize,
        searchToFetch = debouncedSearchTerm,
    } = {}) => {
        try {
            const offset = (Math.max(1, pageToFetch) - 1) * Math.max(1, pageSizeToFetch);
            const params = {
                limit: pageSizeToFetch,
                offset,
            };
            if (searchToFetch) {
                params.search = searchToFetch;
            }

            const data = await api.getOutbounds(params);
            const rows = ExtractResults(data, ['results', 'outbounds']);
            setOutbounds(rows);
            setPaging(ExtractPaging(data, { total: rows.length, limit: pageSizeToFetch, offset }));
        } catch (error) {
            toast.error(error.message || 'Erro ao carregar outbounds');
        } finally {
            setLoading(false);
        }
    };

    const fetchInboundData = async ({
        pageToFetch = inboundPage,
        pageSizeToFetch = inboundPageSize,
        searchToFetch = debouncedSearchTerm,
    } = {}) => {
        try {
            setInboundLoading(true);
            const offset = (Math.max(1, pageToFetch) - 1) * Math.max(1, pageSizeToFetch);
            const params = {
                limit: pageSizeToFetch,
                offset,
            };
            if (searchToFetch) {
                params.search = searchToFetch;
            }

            const data = await api.getWmsInboundTasks(params);
            const rows = ExtractResults(data, ['results', 'inbounds']);
            setInbounds(rows);
            setInboundPaging(ExtractPaging(data, { total: rows.length, limit: pageSizeToFetch, offset }));
        } catch (error) {
            if (error?.status === 403) {
                setInbounds([]);
                setInboundPaging({
                    total: 0,
                    limit: pageSizeToFetch,
                    offset: 0,
                });
                return;
            }

            toast.error(error.message || 'Erro ao carregar inbounds');
        } finally {
            setInboundLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ pageToFetch: page, pageSizeToFetch: pageSize, searchToFetch: debouncedSearchTerm });
        fetchInboundData({
            pageToFetch: inboundPage,
            pageSizeToFetch: inboundPageSize,
            searchToFetch: debouncedSearchTerm,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, inboundPage, inboundPageSize, debouncedSearchTerm]);

    const handleUpdateStatus = async (id, status) => {
        if (!canUpdateStatus) {
            toast.error('Voce nao tem permissao para atualizar status no WMS');
            return;
        }

        try {
            setUpdatingStatus(true);
            await api.updateOutboundStatus(id, status);
            await fetchData();
            await fetchInboundData();
        } catch (error) {
            toast.error(error.message || 'Erro ao atualizar status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const openStatusConfirmation = (outboundId, status) => {
        const label = WMS_STATUS_CONFIG[status]?.label || status;
        setConfirmState({
            isOpen: true,
            outboundId,
            status,
            label,
        });
    };

    const closeStatusConfirmation = (force = false) => {
        if (updatingStatus && !force) {
            return;
        }

        setConfirmState({
            isOpen: false,
            outboundId: null,
            status: null,
            label: '',
        });
    };

    const confirmStatusUpdate = async () => {
        if (!confirmState.outboundId || !confirmState.status) {
            return;
        }

        await handleUpdateStatus(confirmState.outboundId, confirmState.status);
        closeStatusConfirmation(true);
    };

    const openCancelModal = (outboundId) => {
        setCancelState({
            isOpen: true,
            outboundId,
            reason: '',
            confirmationText: '',
            files: [],
        });
    };

    const closeCancelModal = () => {
        if (cancelling) {
            return;
        }

        setCancelState({
            isOpen: false,
            outboundId: null,
            reason: '',
            confirmationText: '',
            files: [],
        });
    };

    const setCancelField = (field, value) => {
        setCancelState((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const handleCancelOutbound = async () => {
        if (!cancelState.outboundId) {
            return;
        }

        const reason = String(cancelState.reason || '').trim();
        if (!reason) {
            toast.error('Informe o motivo do cancelamento.');
            return;
        }

        if (String(cancelState.confirmationText || '').trim() !== 'Cancelar') {
            toast.error('Digite exatamente "Cancelar" para confirmar.');
            return;
        }

        try {
            setCancelling(true);
            const formData = new FormData();
            formData.append('reason', reason);
            formData.append('confirmation_text', String(cancelState.confirmationText || '').trim());
            (cancelState.files || []).forEach((file) => {
                formData.append('files', file);
            });

            await api.cancelOutbound(cancelState.outboundId, formData);
            await fetchData();
            await fetchInboundData();
            closeCancelModal();
            toast.success('Outbound cancelado com sucesso.');
        } catch (error) {
            toast.error(error.message || 'Erro ao cancelar outbound');
        } finally {
            setCancelling(false);
        }
    };

    const openInboundDetails = (row) => {
        setInboundDetailsState({
            isOpen: true,
            row,
        });
    };

    const closeInboundDetails = () => {
        setInboundDetailsState({
            isOpen: false,
            row: null,
        });
    };

    const openInboundReceiveModal = (row) => {
        setInboundReceiveState({
            isOpen: true,
            row,
            files: [],
            note: '',
        });
    };

    const closeInboundReceiveModal = () => {
        if (receivingInbound) {
            return;
        }

        setInboundReceiveState({
            isOpen: false,
            row: null,
            files: [],
            note: '',
        });
    };

    const handleReceiveInbound = async () => {
        if (!inboundReceiveState?.row?.id) {
            return;
        }

        if (!Array.isArray(inboundReceiveState.files) || inboundReceiveState.files.length === 0) {
            toast.error('Anexe pelo menos um arquivo da nota fiscal');
            return;
        }

        const formData = new FormData();
        (inboundReceiveState.files || []).forEach((file) => {
            formData.append('files', file);
        });
        if (String(inboundReceiveState.note || '').trim()) {
            formData.append('note', String(inboundReceiveState.note || '').trim());
        }

        try {
            setReceivingInbound(true);
            await api.receiveWmsInboundTask(inboundReceiveState.row.id, formData);
            toast.success('Inbound recebido com sucesso');
            closeInboundReceiveModal();
            await fetchInboundData();
            await fetchData();
        } catch (error) {
            toast.error(error.message || 'Erro ao confirmar recebimento');
        } finally {
            setReceivingInbound(false);
        }
    };

    const columns = [
        {
            key: 'freight_code',
            label: 'Frete #',
            getValue: (row) => row?.freight_code || '-',
            render: (row) => (
                <span className="font-mono text-black dark:text-white">
                    {row?.freight_code ? `Frete #${row.freight_code}` : '-'}
                </span>
            ),
        },
        {
            key: 'created_at',
            label: 'Data da solicitacao',
            getValue: (row) => row?.created_at || '',
            render: (row) => (
                <span className="font-medium text-black dark:text-white">
                    {FormatDateTimeBr(row.created_at, { blank: '-' })}
                </span>
            ),
        },
        {
            key: 'haulier_name',
            label: 'Transportadora',
            getValue: (row) => row.haulier_name || '-',
        },
        {
            key: 'driver',
            label: 'Motorista',
            getValue: (row) =>
                row?.driver_name ? `${row.driver_name}${row?.driver_document ? ` (${row.driver_document})` : ''}` : '-',
        },
        {
            key: 'vehicle_plate',
            label: 'Placa',
            align: 'center',
            searchable: true,
            getValue: (row) => row.vehicle_plate || '-',
        },
        {
            key: 'total_pallets',
            label: 'Pallets calc.',
            align: 'center',
            searchable: false,
            getValue: (row) => Number.parseFloat(row.total_pallets || 0).toFixed(2),
        },
        {
            key: 'status',
            label: 'Status',
            sortable: false,
            render: (row) => {
                const statusKey = row.display_status || row.status;
                const config = WMS_STATUS_CONFIG[statusKey] || {
                    label: statusKey,
                    color: 'bg-mono-100',
                    icon: Warning,
                };
                const Icon = config.icon;
                return (
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${config.color}`}
                    >
                        <Icon size={14} weight="fill" />
                        {config.label}
                    </span>
                );
            },
        },
        {
            key: 'actions',
            label: 'Acoes',
            align: 'right',
            sortable: false,
            searchable: false,
            exportable: false,
            render: (row) => (
                <div className="flex flex-wrap justify-end gap-2">
                    {canUpdateStatus && row.status === 'idle' ? (
                        <Button
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                openStatusConfirmation(row.id, 'picking');
                            }}
                        >
                            Iniciar separacao
                        </Button>
                    ) : null}

                    {row.status === 'picking' ? (
                        <Button
                            size="sm"
                            variant="primary"
                            className="!bg-green-600 hover:!bg-green-700 !text-white border-none"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/wms/outbound/${row.id}`);
                            }}
                        >
                            <Camera size={14} className="mr-1.5" />
                            Finalizar
                        </Button>
                    ) : null}

                    {canUpdateStatus && row.status === 'ready_to_collect' ? (
                        <Button
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/wms/outbound/${row.id}`);
                            }}
                        >
                            Confirmar coleta
                        </Button>
                    ) : null}

                    {canManageOutbound && !['collected', 'rejected'].includes(String(row.status || '')) ? (
                        <Button
                            size="sm"
                            variant="danger"
                            onClick={(event) => {
                                event.stopPropagation();
                                openCancelModal(row.id);
                            }}
                        >
                            Cancelar
                        </Button>
                    ) : null}

                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/wms/outbound/${row.id}`);
                        }}
                    >
                        Detalhes
                    </Button>
                </div>
            ),
        },
    ];

    const inboundColumns = [
        {
            key: 'code',
            label: 'OC',
            getValue: (row) => row?.code || '-',
            render: (row) => <span className="font-mono text-black dark:text-white">{row?.code || '-'}</span>,
        },
        {
            key: 'shipped_at',
            label: 'Fornecedor enviou em',
            getValue: (row) => row?.shipped_at || '',
            render: (row) => {
                const formatted = FormatDateTimeBr(row?.shipped_at, { blank: '' });
                if (!formatted) {
                    return '-';
                }
                return <span className="font-medium text-black dark:text-white">{formatted}</span>;
            },
        },
        {
            key: 'supplier_name',
            label: 'Fornecedor',
            getValue: (row) => row?.supplier_name || '-',
        },
        {
            key: 'items_count',
            label: 'Itens',
            align: 'center',
            searchable: false,
            getValue: (row) => Number(row?.items_count || 0),
            render: (row) => (
                <span className="text-sm text-black dark:text-white">
                    {Number(row?.items_count || 0)} item(ns) / {Number(row?.total_units || 0)} unid.
                </span>
            ),
        },
        {
            key: 'status',
            label: 'Status',
            getValue: (row) => row?.status || '',
            // Status REAL da OC — hardcoded era mentira p/ OC ainda em OPEN.
            render: (row) => PURCHASING_STATUS_LABELS[row?.status] || row?.status || '-',
        },
        {
            key: 'actions',
            label: 'Acoes',
            align: 'right',
            sortable: false,
            searchable: false,
            exportable: false,
            render: (row) => (
                <div className="flex flex-wrap justify-end gap-2">
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={(event) => {
                            event.stopPropagation();
                            openInboundDetails(row);
                        }}
                    >
                        Ver itens
                    </Button>
                    {/* Receber só existe quando há algo enviado: OC em
                        "Aguardando fornecedor" aparece na fila (visibilidade
                        do que vem aí) mas não tem o que conferir ainda — e o
                        backend recusaria a transição de qualquer forma. */}
                    {canUpdateStatus && row?.status === 'SHIPPED' ? (
                        <Button
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                openInboundReceiveModal(row);
                            }}
                        >
                            Confirmar recebimento
                        </Button>
                    ) : null}
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader title="Warehouse Management (WMS)" subtitle="Visao geral dos outbounds e operacoes de carga" />

            <WmsNavigation />

            <StandardTable
                rows={loading ? [] : outbounds}
                columns={columns}
                rowKey={(row) => row.id}
                countLabel="outbounds"
                emptyMessage={loading ? 'Carregando outbounds...' : 'Nenhum outbound registrado.'}
                searchMode="server"
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                searchPlaceholder="Buscar por frete, SKU, transportadora, motorista, placa ou status..."
                paginationMode="server"
                totalItems={paging.total}
                currentPage={page}
                onPageChange={setPage}
                itemsPerPage={pageSize}
                onItemsPerPageChange={(nextPageSize) => {
                    setPageSize(nextPageSize);
                    setPage(1);
                }}
                pageSizeOptions={[15, 30, 50, 100, 200]}
                onRowClick={(row) => navigate(`/wms/outbound/${row.id}`)}
            />

            <StandardTable
                rows={inboundLoading ? [] : inbounds}
                columns={inboundColumns}
                rowKey={(row) => row.id}
                countLabel="inbounds"
                emptyMessage={inboundLoading ? 'Carregando inbounds...' : 'Nenhum inbound pendente.'}
                searchEnabled={false}
                paginationMode="server"
                totalItems={inboundPaging.total}
                currentPage={inboundPage}
                onPageChange={setInboundPage}
                itemsPerPage={inboundPageSize}
                onItemsPerPageChange={(nextPageSize) => {
                    setInboundPageSize(nextPageSize);
                    setInboundPage(1);
                }}
                pageSizeOptions={[15, 30, 50, 100, 200]}
                onRowClick={(row) => openInboundDetails(row)}
            />

            <ConfirmModal
                isOpen={confirmState.isOpen}
                onClose={closeStatusConfirmation}
                onConfirm={confirmStatusUpdate}
                title="Confirmar atualizacao de status"
                message={
                    confirmState.status
                        ? `Deseja realmente alterar o status desta ordem para "${confirmState.label}"?`
                        : 'Deseja realmente alterar o status desta ordem?'
                }
                confirmText="Atualizar status"
                cancelText="Cancelar"
                loading={updatingStatus}
            />

            <Modal
                isOpen={cancelState.isOpen}
                onClose={closeCancelModal}
                title="Cancelar outbound"
                size="lg"
                actions={
                    <>
                        <Button variant="secondary" onClick={closeCancelModal} disabled={cancelling}>
                            Voltar
                        </Button>
                        <Button variant="danger" onClick={handleCancelOutbound} loading={cancelling}>
                            Confirmar cancelamento
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-mono-600 dark:text-mono-400">
                        Para cancelar, descreva o motivo e digite <strong>Cancelar</strong> para confirmar.
                    </p>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            Motivo do cancelamento
                        </label>
                        <Textarea
                            rows={4}
                            value={cancelState.reason}
                            onChange={(event) => setCancelField('reason', event.target.value)}
                            placeholder="Descreva o motivo..."
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            Confirmacao
                        </label>
                        <Input
                            value={cancelState.confirmationText}
                            onChange={(event) => setCancelField('confirmationText', event.target.value)}
                            placeholder='Digite "Cancelar"'
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            Arquivos (opcional)
                        </label>
                        <Input
                            type="file"
                            multiple
                            onChange={(event) => setCancelField('files', Array.from(event.target.files || []))}
                        />
                        {cancelState.files?.length ? (
                            <p className="mt-2 text-xs text-mono-500 dark:text-mono-400">
                                {cancelState.files.length} arquivo(s) selecionado(s)
                            </p>
                        ) : null}
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={inboundDetailsState.isOpen}
                onClose={closeInboundDetails}
                title={inboundDetailsState?.row?.code ? `Itens da ${inboundDetailsState.row.code}` : 'Itens do inbound'}
                size="lg"
            >
                {Array.isArray(inboundDetailsState?.row?.items) && inboundDetailsState.row.items.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[520px]">
                            <thead className="border-b border-mono-200 dark:border-mono-800">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                        SKU
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                        Solicitado
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                        Confirmado
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-mono-200 dark:divide-mono-800">
                                {inboundDetailsState.row.items.map((item, index) => (
                                    <tr key={`${item.sku}-${index}`}>
                                        <td className="px-3 py-2 text-xs font-mono text-mono-700 dark:text-mono-300">
                                            {item.sku || '-'}
                                        </td>
                                        <td className="px-3 py-2 text-right text-sm text-black dark:text-white">
                                            {Number(item.quantity_ordered || 0)}
                                        </td>
                                        <td className="px-3 py-2 text-right text-sm text-black dark:text-white">
                                            {Number(item.quantity_confirmed || 0)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        Nenhum item encontrado para este inbound.
                    </p>
                )}
            </Modal>

            <Modal
                isOpen={inboundReceiveState.isOpen}
                onClose={closeInboundReceiveModal}
                title={inboundReceiveState?.row?.code ? `Receber ${inboundReceiveState.row.code}` : 'Receber inbound'}
                size="lg"
                actions={
                    <>
                        <Button variant="secondary" onClick={closeInboundReceiveModal} disabled={receivingInbound}>
                            Voltar
                        </Button>
                        <Button onClick={handleReceiveInbound} loading={receivingInbound}>
                            Confirmar recebimento
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-mono-600 dark:text-mono-400">
                        Para finalizar o inbound, anexe ao menos um arquivo de nota fiscal (PDF ou imagem).
                    </p>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            Arquivos
                        </label>
                        <Input
                            type="file"
                            accept="application/pdf,image/*"
                            multiple
                            onChange={(event) =>
                                setInboundReceiveState((current) => ({
                                    ...current,
                                    files: Array.from(event.target.files || []),
                                }))
                            }
                        />
                        {Array.isArray(inboundReceiveState.files) && inboundReceiveState.files.length > 0 ? (
                            <p className="mt-2 text-xs text-mono-500 dark:text-mono-400">
                                {inboundReceiveState.files.length} arquivo(s) selecionado(s)
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            Observacao (opcional)
                        </label>
                        <Textarea
                            rows={3}
                            value={inboundReceiveState.note}
                            onChange={(event) =>
                                setInboundReceiveState((current) => ({
                                    ...current,
                                    note: event.target.value,
                                }))
                            }
                            placeholder="Observacoes do recebimento"
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DashboardPage;
